import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../../config/api";
import { BASE_API } from "../../../../constants/apiUrls";
import { toast } from "../../../../lib/toast";
import {
  CommonOthers,
  CommonParams,
  ErrorMessage,
  GetCommonData,
  GetSingleDataParams,
  Params,
  PostRequest,
} from "../../../../types";
import {
  createDistrictData,
  districtData,
} from "../../../../types/geo/geo";
import { handleApiError } from "../../../../utils/errorHandler";

// create district
export const createDistrictAsync = createAsyncThunk<
  null,
  PostRequest<createDistrictData, CommonOthers>
>(
  "admin-geo/createDistrict",
  async ({ data, others: { reRender } }, { rejectWithValue }) => {
    try {
      await AXIOS.post(BASE_API.createDistrict, data);
      reRender && reRender(true);
      toast("success", "District created successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "District create failed");
      return rejectWithValue(error);
    }
  }
);

// get all district
export const getAllDistrictListAsync = createAsyncThunk<
  GetCommonData<districtData>,
  Params<
    CommonParams & {
      division_id?: string;
      districtName?: string;
      custom_text?: string;
    }
  >,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/getAllDistrictList", async ({ params }, { rejectWithValue }) => {
  const { ...rest } = params || {};

  try {
    const data = await AXIOS.get(BASE_API.getDistrict, {
      params: { ...rest },
    });

    return {
      data: data.data?.getData || [],
      length: data.data?.totalCount ?? (data.data?.getData?.length || 0),
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Get district failed");
    return rejectWithValue(error);
  }
});

// get single district
export const getSingleDistrictAsync = createAsyncThunk<
  districtData,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/getSingleDistrict", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.getSingleDistrict}/${params?.id}`);
    return data.data?.getData;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "Get single district failed");
    return rejectWithValue(error);
  }
});

// update district
export const updateDistrictAsync = createAsyncThunk<
  null,
  PostRequest<createDistrictData, GetSingleDataParams>
>(
  "admin-geo/updateDistrict",
  async ({ data, others: { reRender, id } }, { rejectWithValue }) => {
    try {
      await AXIOS.patch(`${BASE_API.updateDistrict}/${id}`, data);
      reRender && reRender(true);
      toast("success", "District updated successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      handleApiError(error, "District update failed");
      return rejectWithValue(error);
    }
  }
);

// delete district
export const deleteDistrictAsync = createAsyncThunk<
  null,
  Params<GetSingleDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin-geo/deleteDistrict", async ({ params }, { rejectWithValue }) => {
  try {
    await AXIOS.delete(`${BASE_API.deleteDistrict}/${params?.id}`);
    params?.reRender && params?.reRender(true);
    toast("success", "District deleted successfully");
    return null;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    handleApiError(error, "District delete failed");
    return rejectWithValue(error);
  }
});

